/**
 * Draft Management Drawer Component
 * 
 * Full-featured draft management with:
 * - List all drafts with preview
 * - Individual delete
 * - Delete all drafts
 * - Filter by mode
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, Timeline, Button, Tag, Space, Typography, Empty, Popconfirm, message, Flex, Input } from 'antd';
import { FileTextOutlined, ClockCircleOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import { DraftInfo } from '../../hooks/useDraftDetection';
import { clearUserDrafts, getCurrentUserAndOrg } from '../../utils/storageUtils';
import './DraftManagementModal.scss';

const { Search } = Input;

const { Text, Title } = Typography;

interface DraftManagementModalProps {
  visible: boolean;
  onClose: () => void;
  drafts: DraftInfo[];
  onRestore: (draft: DraftInfo) => void;
  onDelete: (draft: DraftInfo) => void;
}

const DraftManagementModal: React.FC<DraftManagementModalProps> = ({
  visible,
  onClose,
  drafts,
  onRestore,
  onDelete,
}) => {
  const [filterMode, setFilterMode] = useState<'all' | 'new' | 'edit' | 'clone'>('all');
  const [searchValue, setSearchValue] = useState<string>('');
  const [drawerWidth, setDrawerWidth] = useState<string | number>('50%');

  // Handle responsive width
  useEffect(() => {
    const updateWidth = () => {
      setDrawerWidth(window.innerWidth < 768 ? '80%' : '50%');
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Format last saved time
  const formatLastSaved = (timestamp?: number) => {
    if (!timestamp) return 'Unknown time';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleString();
  };

  // Filter and sort drafts by mode and search, then by most recent first
  const filteredDrafts = useMemo(() => {
    let filtered = filterMode === 'all' 
    ? drafts 
    : drafts.filter(draft => draft.mode === filterMode);
    
    // Apply search filter by title or description (position)
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      filtered = filtered.filter(draft => {
        const title = draft.preview?.title?.toLowerCase() || '';
        const description = draft.preview?.position?.toLowerCase() || '';
        return title.includes(searchLower) || description.includes(searchLower);
      });
    }
    
    // Sort by lastSavedAt descending (most recent first)
    return [...filtered].sort((a, b) => {
      const timeA = a.lastSavedAt || 0;
      const timeB = b.lastSavedAt || 0;
      return timeB - timeA;
    });
  }, [drafts, filterMode, searchValue]);

  // Handle delete all
  const handleDeleteAll = () => {
    const { userId, orgId } = getCurrentUserAndOrg();
    if (!userId || !orgId) {
      message.error('Unable to delete drafts: User not identified');
      return;
    }
    
    const deletedCount = clearUserDrafts(userId, orgId);
    if (deletedCount > 0) {
      message.success(`Deleted ${deletedCount} draft${deletedCount > 1 ? 's' : ''}`);
      // Trigger storage event to update draft list
      window.dispatchEvent(new Event('storage'));
      onClose();
    } else {
      message.info('No drafts to delete');
    }
  };

  return (
    <Drawer
      title={null}
      placement="right"
      onClose={onClose}
      open={visible}
      width={drawerWidth}
      closable={false}
      className="draft-management-drawer"
      styles={{
        body: {
          padding: 24,
        },
      }}
    >
      <div className="draft-management-drawer-content">
        {/* Header Row */}
        <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
          <Title level={4} style={{ margin: 0 }}>
        <Space>
          <FileTextOutlined />
          <span>Manage Drafts</span>
          {drafts.length > 0 && (
            <Tag color="blue">{drafts.length} draft{drafts.length > 1 ? 's' : ''}</Tag>
          )}
        </Space>
          </Title>
          <Button 
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
            aria-label="Close"
          />
        </Flex>
        {/* Search and Filter Section */}
      {drafts.length > 0 && (
        <div className="draft-filters" style={{ marginBottom: 16 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Search
                placeholder="Search by title or description"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                allowClear
                style={{ width: '100%' }}
              />
              <Space wrap>
            <Button 
              type={filterMode === 'all' ? 'primary' : 'default'}
              onClick={() => setFilterMode('all')}
            >
              All ({drafts.length})
            </Button>
            <Button 
              type={filterMode === 'new' ? 'primary' : 'default'}
              onClick={() => setFilterMode('new')}
            >
              New ({drafts.filter(d => d.mode === 'new').length})
            </Button>
            <Button 
              type={filterMode === 'edit' ? 'primary' : 'default'}
              onClick={() => setFilterMode('edit')}
            >
              Edit ({drafts.filter(d => d.mode === 'edit').length})
            </Button>
            <Button 
              type={filterMode === 'clone' ? 'primary' : 'default'}
              onClick={() => setFilterMode('clone')}
            >
              Clone ({drafts.filter(d => d.mode === 'clone').length})
            </Button>
              </Space>
          </Space>
        </div>
      )}

        {/* Draft Timeline List */}
        <div className="draft-management-drawer-list">
          {filteredDrafts.length > 0 ? (
            <Timeline mode="left">
              {filteredDrafts.map((draft) => (
                <Timeline.Item key={draft.storageKey || `${draft.mode}-${draft.lastSavedAt}`}>
                  <div className="draft-timeline-item">
                    <div className="draft-timeline-content">
                      <div className="draft-timeline-header">
                        <Space>
                          <Text strong>{draft.preview?.title || 'Untitled Job Post'}</Text>
                          {draft.mode === 'edit' && draft.jobPostId && (
                            <Tag color="blue">Edit</Tag>
                          )}
                          {draft.mode === 'clone' && (
                            <Tag color="green">Clone</Tag>
                          )}
                          {draft.mode === 'new' && (
                            <Tag color="orange">New</Tag>
                          )}
                        </Space>
                      </div>
                      <div className="draft-timeline-description">
                        <Space split={<span>•</span>}>
                          <Text type="secondary">{draft.preview?.position || 'No position'}</Text>
                          <Text type="secondary">Step {draft.step || 1}</Text>
                          <Space size={4}>
                            <ClockCircleOutlined style={{ fontSize: 12 }} />
                            <Text type="secondary">{formatLastSaved(draft.lastSavedAt)}</Text>
                          </Space>
                        </Space>
                      </div>
                      <div className="draft-timeline-actions">
                        <Space>
                          <Button 
                            type="primary" 
                            onClick={() => {
                              onRestore(draft);
                              onClose();
                            }}
                          >
                            Continue
                          </Button>
                          <Popconfirm
                            title="Delete this draft?"
                            description="This action cannot be undone."
                            onConfirm={() => {
                              onDelete(draft);
                              message.success('Draft deleted');
                            }}
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                          >
                            <Button danger icon={<DeleteOutlined />}>
                              Delete
                            </Button>
                          </Popconfirm>
                        </Space>
                      </div>
                    </div>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : (
            <Empty description="No drafts found" />
          )}
        </div>

        {/* Delete All Button - Fixed at bottom */}
        {drafts.length > 0 && (
          <div className="draft-management-drawer-footer">
            <Popconfirm
              title="Delete all drafts?"
              description="This will permanently delete all your saved drafts. This action cannot be undone."
              onConfirm={handleDeleteAll}
              okText="Yes, Delete All"
              okButtonProps={{ danger: true }}
            >
              <Button 
                danger 
                icon={<DeleteOutlined />}
              >
                Delete All Drafts
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default DraftManagementModal;

